import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Shield, User } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";
import { UserRestrictionsFields, AccessBadge } from "@/components/UserRestrictionsFields";

const blankForm = { email: "", password: "", name: "", role: "user", allowed_provinces: [], valid_until: "" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [editForm, setEditForm] = useState({ name: "", role: "user", password: "", allowed_provinces: [], valid_until: "" });
  const [provinces, setProvinces] = useState([]);
  const provinceNames = new Map(provinces.map((p) => [p.id, p.name]));

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    api.get("/zones?level=provincia&limit=100").then(({ data }) =>
      setProvinces([...(data.zones || [])].sort((a, b) => a.name.localeCompare(b.name, "es")))
    );
  }, []);

  const create = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Email, contraseña y nombre son obligatorios");
      return;
    }
    if (form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      const body = { ...form, valid_until: form.valid_until || null };
      if (form.role === "admin") Object.assign(body, { allowed_provinces: [], valid_until: null });
      await api.post("/admin/users", body);
      toast.success(`Usuario ${form.email} creado`);
      setCreateOpen(false);
      setForm(blankForm);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo crear");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const body = {};
    if (editForm.name && editForm.name !== editing.name) body.name = editForm.name;
    if (editForm.role && editForm.role !== editing.role) body.role = editForm.role;
    if (editForm.password) body.password = editForm.password;
    const isUser = (editForm.role || editing.role) === "user";
    const provs = isUser ? editForm.allowed_provinces : [];
    const vu = isUser ? editForm.valid_until || null : null;
    if ([...provs].sort().join() !== [...(editing.allowed_provinces || [])].sort().join()) body.allowed_provinces = provs;
    if (vu !== (editing.valid_until || null)) body.valid_until = vu;
    if (Object.keys(body).length === 0) {
      toast.info("Sin cambios");
      return;
    }
    try {
      await api.patch(`/admin/users/${editing.id}`, body);
      toast.success("Usuario actualizado");
      setEditing(null);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar");
    }
  };

  const remove = async (u) => {
    if (!confirm(`¿Eliminar el usuario ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="h-full overflow-auto p-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="label-micro text-white/40">ADMINISTRACIÓN</p>
          <h1 className="text-2xl font-bold mt-1">Gestión de usuarios</h1>
          <p className="text-white/50 text-sm mt-1">
            Crea cuentas, asigna roles y limita el acceso de clientes por provincia y fecha de caducidad. Los usuarios <span className="text-[#00E5FF]">user</span> pueden consultar matrices; los <span className="text-[#FF9800]">admin</span> también gestionan otros usuarios.
          </p>
        </div>
        <Button
          onClick={() => {
            setForm(blankForm);
            setCreateOpen(true);
          }}
          data-testid="create-user-btn"
          className="rounded-none bg-[#FF9800] hover:bg-[#FFA726] text-black font-semibold btn-press"
        >
          <Plus size={14} weight="bold" className="mr-2" />
          Nuevo usuario
        </Button>
      </div>

      <div className="border border-white/10 bg-[#121212]" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] border-b border-white/10">
            <tr>
              <th className="text-left px-4 py-3 label-micro">EMAIL</th>
              <th className="text-left px-4 py-3 label-micro">NOMBRE</th>
              <th className="text-left px-4 py-3 label-micro">ROL</th>
              <th className="text-left px-4 py-3 label-micro">ACCESO</th>
              <th className="text-left px-4 py-3 label-micro">CREADO</th>
              <th className="text-right px-4 py-3 label-micro">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40 font-mono text-xs">CARGANDO…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">Sin usuarios</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono text-xs text-white">{u.email}</td>
                <td className="px-4 py-3 text-white/80">{u.name}</td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#FF9800] text-[#FF9800] font-mono text-[10px] uppercase tracking-[0.2em]">
                      <Shield size={10} weight="bold" /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-white/20 text-white/60 font-mono text-[10px] uppercase tracking-[0.2em]">
                      <User size={10} weight="bold" /> User
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <AccessBadge user={u} provinceNames={provinceNames} />
                </td>
                <td className="px-4 py-3 text-white/40 font-mono text-[11px]">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      setEditing(u);
                      setEditForm({ name: u.name || "", role: u.role || "user", password: "", allowed_provinces: u.allowed_provinces || [], valid_until: u.valid_until || "" });
                    }}
                    data-testid={`edit-user-${u.email}`}
                    className="p-1.5 border border-white/10 hover:border-[#FF9800] mr-1 btn-press"
                    title="Editar"
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    onClick={() => remove(u)}
                    disabled={u.id === me?.id}
                    data-testid={`delete-user-${u.email}`}
                    className="p-1.5 border border-white/10 hover:border-[#F44336] hover:text-[#F44336] disabled:opacity-30 btn-press"
                    title={u.id === me?.id ? "No puedes eliminarte" : "Eliminar"}
                  >
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear nuevo usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-micro">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="usuario@empresa.com"
                className="rounded-none bg-[#121212] border-white/10 mt-1"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label className="label-micro">Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre completo"
                className="rounded-none bg-[#121212] border-white/10 mt-1"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label className="label-micro">Contraseña (mínimo 6 caracteres)</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none bg-[#121212] border-white/10 mt-1"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label className="label-micro">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none bg-[#121212] border-white/10 mt-1" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuario (consultas)</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "user" && (
              <UserRestrictionsFields value={form} onChange={(v) => setForm({ ...form, ...v })} provinces={provinces} testPrefix="new-user" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-none border-white/10 bg-transparent">
              Cancelar
            </Button>
            <Button onClick={create} className="rounded-none bg-[#FF9800] text-black hover:bg-[#FFA726]" data-testid="confirm-create-user">
              Crear usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar usuario · {editing?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-micro">Nombre</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="rounded-none bg-[#121212] border-white/10 mt-1"
                data-testid="edit-user-name"
              />
            </div>
            <div>
              <Label className="label-micro">Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className="rounded-none bg-[#121212] border-white/10 mt-1" data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuario (consultas)</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-micro">Nueva contraseña (opcional)</Label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Dejar vacío para no cambiarla"
                className="rounded-none bg-[#121212] border-white/10 mt-1"
                data-testid="edit-user-password"
              />
            </div>
            {editForm.role === "user" && (
              <UserRestrictionsFields value={editForm} onChange={(v) => setEditForm({ ...editForm, ...v })} provinces={provinces} testPrefix="edit-user" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none border-white/10 bg-transparent">
              Cancelar
            </Button>
            <Button onClick={saveEdit} className="rounded-none bg-[#FF9800] text-black hover:bg-[#FFA726]" data-testid="confirm-edit-user">
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
